"use client";

import React from "react";
import { Zap, ShieldCheck, Cpu, Leaf, ArrowRight, CircleCheck as CheckCircle2, Wrench, Clock } from 'lucide-react';
import { motion, useReducedMotion } from "framer-motion";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardHeader, CardTitle, CardDescription, CardContent, CardFooter } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
const featuredBrands = [{
  name: "Rheem",
  tagline: "Performance & Hybrid Electric Leader",
  description: "Full service and diagnostics for Rheem standard electric tanks, Marathon non-metallic units, and ProTerra hybrid electric heat pumps.",
  features: "Dual heating elements, EcoNet smart controls, hybrid diagnostics",
  badgeText: "Factory Spec Serviced"
}, {
  name: "Bradford White",
  tagline: "Commercial-Grade Residential Reliability",
  description: "Certified maintenance and repair for Bradford White Hydrojet electric storage heaters and high-demand residential systems.",
  features: "Defender Safety System, sediment-reduction technology, rapid heating recovery",
  badgeText: "Contractor Grade"
}, {
  name: "A.O. Smith",
  tagline: "Innovative Efficiency & Voltex Hybrid Systems",
  description: "Comprehensive care for Signature series, ProLine master electric heaters, and Voltex high-efficiency hybrid heat pump models.",
  features: "Electronic user interfaces, anode rod protection, smart leak detection",
  badgeText: "Energy Star Partner"
}, {
  name: "Navien",
  tagline: "Advanced Electric Controls & Next-Gen Systems",
  description: "Specialized servicing for modern high-efficiency systems and cutting-edge digital electric control configurations.",
  features: "Precision temperature regulation, stainless steel heat exchangers, compact footprint",
  badgeText: "Smart Integration"
}];
const expertiseHighlights = [{
  icon: Zap,
  title: "Electric System Mastery",
  description: "From upper and lower heating elements to high-limit thermostats and dedicated electrical circuits, our plumbers diagnose complex electrical faults accurately."
}, {
  icon: Leaf,
  title: "Energy Efficiency Optimization",
  description: "We configure hybrid electric heat pumps and programmable timers to lower monthly utility bills while maintaining consistent hot water supply."
}, {
  icon: Cpu,
  title: "Brand Familiarity & Genuine Parts",
  description: "Direct access to OEM thermostats, dry-fire protection elements, and manufacturer wiring diagrams ensures long-lasting repairs."
}];
export default function ElectricBrands() {
  const shouldReduceMotion = useReducedMotion();
  const containerVariants = {
    hidden: {
      opacity: 0
    },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: shouldReduceMotion ? 0 : 0.12,
        delayChildren: 0.1
      }
    }
  };
  const itemVariants = {
    hidden: {
      opacity: 0,
      y: shouldReduceMotion ? 0 : 16
    },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.45,
        ease: "easeOut"
      }
    }
  };
  return <section id="electricwaterheaterbrands" data-nav="light" className="bg-background text-foreground py-16 md:py-24 border-b border-border transition-colors">
      <div className="container mx-auto px-4 md:px-6 max-w-7xl">
        {/* Section Header */}
        <div className="max-w-3xl mx-auto text-center mb-14 md:mb-18">
          <Badge variant="secondary" className="mb-3 px-3 py-1 text-xs md:text-sm font-semibold tracking-wide uppercase">
            <Zap className="size-3.5 text-accent mr-1" />
            Electric Water Heater Specialists
          </Badge>
          <h2 className="font-default text-3xl md:text-4xl lg:text-5xl font-bold tracking-tight text-foreground mt-2">
            Brands We Service & Install in Jonesboro
          </h2>
          <p className="font-default text-muted-foreground text-base md:text-lg mt-4 leading-relaxed">
            Our licensed technicians handle diagnostics, component replacement, and new installations for leading electric and hybrid water heater manufacturers.
          </p>
        </div>

        {/* Brand Cards Grid */}
        <motion.div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-16" variants={containerVariants} initial="hidden" whileInView="visible" viewport={{
        once: true,
        margin: "-60px"
      }}>
          {featuredBrands.map((brand, i) => <motion.div key={brand.name} variants={itemVariants} data-index={i} className="h-full flex">
              <Card className="flex flex-col justify-between w-full border border-border/80 bg-card hover:border-primary/40 hover:shadow-md transition-all">
                <CardHeader className="space-y-2 pb-3">
                  <div className="flex items-center justify-between gap-2">
                    <Badge variant="outline" className="text-xs bg-muted/60 text-muted-foreground border-border">
                      {brand.badgeText}
                    </Badge>
                    <ShieldCheck className="size-4 text-accent shrink-0" />
                  </div>
                  <CardTitle className="font-default text-2xl font-bold text-foreground">
                    {brand.name}
                  </CardTitle>
                  <CardDescription className="font-default text-xs font-medium text-secondary-foreground">
                    {brand.tagline}
                  </CardDescription>
                </CardHeader>

                <CardContent className="space-y-4 pt-1 pb-4 flex-1">
                  <p className="font-default text-sm text-muted-foreground leading-relaxed">
                    {brand.description}
                  </p>
                  
                  <Separator />

                  <div className="space-y-1.5">
                    <span className="font-default text-xs font-semibold text-foreground tracking-wide block">
                      Key Capabilities:
                    </span>
                    <p className="font-default text-xs text-muted-foreground leading-snug">
                      {brand.features}
                    </p>
                  </div>
                </CardContent>

                <CardFooter className="pt-2 border-t border-border/50">
                  <Button asChild variant="ghost" size="sm" className="w-full justify-between px-2 text-primary hover:text-primary hover:bg-secondary/60 font-semibold text-xs">
                    <Link to="/brands/">
                      <span>Explore {brand.name} Services</span>
                      <ArrowRight className="size-3.5 ml-1" />
                    </Link>
                  </Button>
                </CardFooter>
              </Card>
            </motion.div>)}
        </motion.div>

        {/* Why Choose Section for Electric Systems */}
        <div className="bg-secondary/40 rounded-2xl border border-border p-6 md:p-10 mb-14">
          <div className="max-w-2xl mb-8">
            <h3 className="font-default text-2xl md:text-3xl font-bold text-foreground tracking-tight">
              Why Homeowners Trust Our Electric Water Heater Team
            </h3>
            <p className="font-default text-muted-foreground text-sm md:text-base mt-2">
              Electric water heating requires a precise balance of electrical wiring safety, thermal calibration, and plumbing plumbing code compliance.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {expertiseHighlights.map((item, i) => {
            const IconComponent = item.icon;
            return <div key={item.title} data-index={i} className="bg-card rounded-xl p-5 md:p-6 border border-border shadow-xs flex flex-col justify-between">
                  <div className="space-y-3">
                    <div className="size-10 rounded-lg bg-primary/10 flex items-center justify-center text-primary">
                      <IconComponent className="size-5" />
                    </div>
                    <h4 className="font-default text-lg font-bold text-card-foreground">
                      {item.title}
                    </h4>
                    <p className="font-default text-sm text-muted-foreground leading-relaxed">
                      {item.description}
                    </p>
                  </div>
                  <div className="mt-4 flex items-center gap-1.5 text-xs text-primary font-medium">
                    <CheckCircle2 className="size-3.5 text-accent" />
                    <span>Certified service standards</span>
                  </div>
                </div>;
          })}
          </div>
        </div>

        {/* Bottom CTA Strip */}
        <div className="flex flex-col sm:flex-row items-center justify-between gap-6 p-6 md:p-8 bg-primary text-primary-foreground rounded-2xl shadow-sm">
          <div className="space-y-1 text-center sm:text-left">
            <h3 className="font-default text-xl md:text-2xl font-bold tracking-tight">
              Need urgent service on your electric water heater?
            </h3>
            <p className="font-default text-sm text-primary-foreground/80 max-w-xl">
              From thermostat failures to complete tank replacements, our emergency plumbers are available 24/7.
            </p>
          </div>
          <div className="flex flex-wrap items-center justify-center gap-3 shrink-0">
            <Button asChild variant="secondary" size="lg" className="font-semibold">
              <Link to="/brands/">
                View All Brands
              </Link>
            </Button>
            <Button asChild variant="outline" size="lg" className="bg-transparent text-primary-foreground border-primary-foreground/30 hover:bg-primary-foreground/10 hover:text-primary-foreground">
              <Link to="/contact/">
                <Clock className="size-4 mr-2" />
                Schedule Service
              </Link>
            </Button>
          </div>
        </div>
      </div>
    </section>;
}